import * as React from "react";
import { observer } from "mobx-react-lite";
import { classNames } from "src/common/classNames";
import { Empty } from "antd";
import styles from "./Mc2Table.module.less";
import { Mc2EntityStore } from "../Mc2EntityStore";
import { Mc2Column } from "./Mc2Column";
import { sizeColumnKey } from "./buildMc2Columns";

interface PropsMc2Table {
  store: Mc2EntityStore;
}

export const Mc2Table: React.FC<PropsMc2Table> = observer(({ store }) => {
  const { table, columns } = store;
  const gridTemplateColumns = makeGridTemplates(columns);
  const baseSizeKey = sizeColumnKey(store.mcEntity.baseSize);

  const refHeader = React.useRef<HTMLDivElement>(null);
  const refBody = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const body = refBody.current;

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === body) {
          if (refHeader.current) {
            refHeader.current.style.width = `${entry.contentRect.width}px`;
          }
        }
      });
    });

    if (body) {
      resizeObserver.observe(body);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [refBody.current]);

  return (
    <div className={styles.tableBox}>
      {/* Header */}
      <div
        className={styles.header}
        ref={refHeader}
        style={{ gridTemplateColumns }}
      >
        {columns.map(({ key, title, titleAlign }, i) => (
          <div
            key={key}
            className={classNames([
              styles.th,
              styles.borderTop,
              styles.borderRB,
              [i === 0, styles.borderLeft],
              [key === baseSizeKey, styles.baseSizeCell],
              clsAlign(titleAlign),
            ])}
          >
            {title}
          </div>
        ))}
      </div>

      <div
        ref={refBody}
        className={styles.body}
        onScroll={(e) => {
          if (refHeader.current) {
            refHeader.current.scrollLeft = e.currentTarget.scrollLeft;
          }
        }}
      >
        <div className={styles.cells} style={{ gridTemplateColumns }}>
          {table.rows.length === 0 && (
            <div
              className={classNames([
                styles.borderLeft,
                styles.borderRB,
                styles.emptyBox,
              ])}
              style={{ gridColumn: `span ${columns.length}` }}
            >
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )}
          {table.rows.map((row) => (
            <React.Fragment key={row.id}>
              {columns.map(({ key, render, align }, j) => (
                <div
                  key={key}
                  className={classNames([
                    styles.borderRB,
                    [j === 0, styles.borderLeft],
                    [key === baseSizeKey, styles.baseSizeCell],
                    clsAlign(align),
                  ])}
                >
                  {render(row)}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
});

const makeGridTemplates = (columns: Mc2Column[]): string =>
  columns
    .map(({ width, minWidth }) => {
      if (!width) {
        return `${minWidth}px`;
      }
      return `minmax(${minWidth}px,${width})`;
    })
    .join(" ");

const clsAlign = (align: "center" | "left" | "right" | undefined) =>
  align ? styles[`${align}Align`] : undefined;
